import BubbleCommon from '@op/components/BubbleCommon';
import EChartCommon from '@op/components/EChartCommon';
import { SENTIMENT_COLOR } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import { LiveChatType } from '@op/pages/RealtimeStreaming/type';
import { DBO_V } from '@op/utils/format';
import React, { useMemo } from 'react';

export default function PieMini({
  data,
  liveTime,
}: {
  data?: LiveChatType;
  liveTime?: string;
}) {
  const { formatMessage } = useLanguage();

  const { options, list, positiveRatio, negativeRatio } = useMemo(() => {
    const { positive = 0, negative = 0, neutral = 0 } = data || {};

    const options = {
      tooltip: { show: false },
      series: [
        {
          data: [
            {
              value: positive,
              name: 'Positive',
              itemStyle: {
                color: SENTIMENT_COLOR.POSITIVE,
              },
            },
            {
              value: negative,
              name: 'Negative',
              itemStyle: {
                color: SENTIMENT_COLOR.NEGATIVE,
              },
            },
            {
              value: neutral,
              name: 'Neutral',
              itemStyle: {
                color: SENTIMENT_COLOR.NEUTRAL,
              },
            },
          ],
          label: { show: false },
        },
      ],
      legend: { show: false },
    };
    const total = positive + negative + neutral;
    const positiveRatio = DBO_V.percentage(positive, total, 2);
    const negativeRatio = DBO_V.percentage(negative, total, 2);
    const neutralRatio = DBO_V.percentage(neutral, total, 2);
    return {
      options,
      list: [
        { text: 'Mentions', value: total },
        {
          text: 'Positive',
          value: positive,
          ratio: positiveRatio,
          color: SENTIMENT_COLOR.POSITIVE,
        },
        {
          text: 'Negative',
          value: negative,
          ratio: negativeRatio,
          color: SENTIMENT_COLOR.NEGATIVE,
        },
        {
          text: 'Neutral',
          value: neutral,
          ratio: neutralRatio,
          color: SENTIMENT_COLOR.NEUTRAL,
        },
      ],
      positiveRatio,
      negativeRatio,
      neutralRatio,
    };
  }, [data, formatMessage]);

  function chartTipsElement() {
    return (
      <div className="chart-common-formatter">
        <div className="chart-common-formatter-header">
          <div className="chart-common-formatter-header-left">{liveTime}</div>
          <div className="chart-common-formatter-header-right"></div>
        </div>
        <table>
          <tbody>
            {list.map(({ color, text, value, ratio }) => (
              <tr key={text}>
                <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                  {color && (
                    <span className="chart-common-formatter-marker">
                      <span
                        className="chart-common-marker-dot"
                        style={{ backgroundColor: color }}
                      ></span>
                    </span>
                  )}
                  {formatMessage({ id: text })}
                </td>
                <td
                  className="bold"
                  style={{ textAlign: 'right', fontWeight: 'bold' }}
                >
                  {DBO_V.reviews(value)}
                  {ratio && `(${ratio})`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <BubbleCommon
      className="dbo-noborer-wrap"
      placement="top"
      showIcon={false}
      content={chartTipsElement()}
      tipsMap={undefined}
    >
      <div className="dbo-pie-mini-wrapper">
        <EChartCommon
          hasEmptyImg={false}
          hasEmptyTitle={false}
          options={options}
          className="dbo-pie-mini-chart"
          height={128}
          optionsProps={{ type: 'pie' }}
        ></EChartCommon>
        <div className="dbo-pie-mini-info">
          <p className="dbo-pie-mini-info-title">{formatMessage('Negative')}</p>
          <p className="dbo-pie-mini-info-value dbo-no-invert">{negativeRatio}</p>
          <p className="dbo-pie-mini-info-title">{formatMessage('Positive')}</p>
          <p className="dbo-pie-mini-info-value positive dbo-no-invert">{positiveRatio}</p>
        </div>
      </div>
    </BubbleCommon>
  );
}
